import React, { useCallback, useState } from "react";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadPliego, analyzeTender } from "@/lib/api";

export const PdfUploader = ({ tenderId, onUploaded, autoAnalyze = true }) => {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleFiles = useCallback(
        async (files) => {
            const list = Array.from(files || []).filter((f) => /\.pdf$/i.test(f.name));
            if (!list.length) {
                toast.error("Solo se aceptan archivos PDF");
                return;
            }
            setUploading(true);
            try {
                let updated;
                for (const f of list) {
                    updated = await uploadPliego(tenderId, f);
                }
                toast.success(`${list.length} pliego(s) subido(s)`);
                onUploaded?.(updated);
                // Auto-fire the AI analysis right after the upload so the user
                // doesn't have to click a separate "Analizar con IA" button.
                if (autoAnalyze) {
                    try {
                        const t = await analyzeTender(tenderId);
                        onUploaded?.(t);
                        toast.info(
                            "Analizando con IA — el resumen y el escandallo aparecerán en 1-2 minutos…",
                            { duration: 6000 },
                        );
                    } catch (e) {
                        toast.error(
                            e?.response?.data?.detail ||
                                "Pliego subido pero el análisis IA no pudo iniciarse",
                        );
                    }
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Error al subir el pliego");
            } finally {
                setUploading(false);
            }
        },
        [tenderId, onUploaded, autoAnalyze]
    );

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    return (
        <label
            onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`relative block cursor-pointer border-2 border-dashed rounded-2xl p-10 transition-all text-center ${
                dragging
                    ? "border-brand-600 bg-brand-50 scale-[1.01]"
                    : "border-ink-200 bg-white hover:border-brand-400 hover:bg-brand-50/40"
            }`}
            data-testid="pdf-dropzone"
        >
            <input
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                disabled={uploading}
                data-testid="pdf-file-input"
            />
            <div className="flex flex-col items-center gap-3">
                {uploading ? (
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                        <Loader2 className="animate-spin" size={24} />
                    </div>
                ) : (
                    <div
                        className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop transition-transform ${
                            dragging ? "scale-110" : ""
                        }`}
                    >
                        <UploadCloud size={24} strokeWidth={2.2} />
                    </div>
                )}
                <div className="font-display text-xl font-bold text-ink-950">
                    {uploading ? "Subiendo pliego…" : "Arrastra el pliego aquí"}
                </div>
                <div className="text-sm text-ink-500 font-medium">
                    o haz clic para seleccionar un PDF (PCAP, PPT o consolidado)
                </div>
            </div>
        </label>
    );
};

export default PdfUploader;
